import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const detail = err.response?.data?.detail || '';
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/' && window.location.pathname !== '/' && window.location.pathname !== '/register') {
        // Store session-expired message for display on login page
        if (detail.includes('another device') || detail.includes('Session expired')) {
          localStorage.setItem('session_expired', 'Your account was logged in from another device. Please login again.');
        }
        window.location.href = '/';
      }
    }
    return Promise.reject(err);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
 login: (data) => {
  const payload = {};
  const identifier = (data.email || '').trim();

  // ✅ detect user_id (2 letters + 4 digits OR mixed like yours)
  if (/^[A-Z0-9]{6}$/i.test(identifier) && !identifier.includes("@")) {
    payload.user_id = identifier.toUpperCase();
  } else {
    payload.email = identifier;
  }

  payload.password = data.password;
  payload.captcha_token = data.captcha_token;

  return api.post('/auth/login', payload);
},
  demoRegister: () => api.post('/auth/demo'),
  googleLogin: (data) => api.post('/auth/google', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  uploadProfilePicture: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/user/profile-picture', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  googleCallback: (data) => api.post('/auth/google-callback', data),
  verify2fa: (data, tempToken) => api.post('/auth/verify-2fa', data, { headers: { 'X-Temp-Token': tempToken } }),
  setup2fa: () => api.post('/auth/2fa/setup'),
  enable2fa: (data) => api.post('/auth/2fa/enable', data),
  disable2fa: (data) => api.post('/auth/2fa/disable', data),
  getSessions: () => api.get('/auth/sessions'),
  revokeSession: (id) => api.delete(`/auth/sessions/${id}`),
};

// Market
export const marketAPI = {
  getTopCoins: (limit = 20) => api.get(`/market/top-coins?limit=${limit}`),
  getPrice: (coinId) => api.get(`/market/price/${coinId}`),
  getHistorical: (coinId, days = 30) => api.get(`/market/historical/${coinId}?days=${days}`),
  getChart: (symbolId, period = '15m', limit = 200) => api.get(`/market/chart/${symbolId}?period=${period}&limit=${limit}`),
  search: (q) => api.get(`/market/search?q=${q}`),
  getCategories: () => api.get('/market/categories'),
};

// Trading
export const tradeAPI = {
  place: (data) => api.post('/trades/place', data),
  close: (tradeId) => api.post(`/trades/close/${tradeId}`),
  getPositions: () => api.get('/trades/positions'),
  getHistory: (limit = 50) => api.get(`/trades/history?limit=${limit}`),
};

// Leverage
export const leverageAPI = {
  place: (data) => api.post('/leverage/place', data),
  getPositions: () => api.get('/leverage/positions'),
};

// Trading Engine
export const tradingAPI = {
  getAccount: () => api.get('/trading/account'),
  placeOrder: (data) => api.post('/trading/order', data),
  closePosition: (id) => api.post(`/trading/close/${id}`),
  getPositions: () => api.get('/trading/positions'),
  getPending: () => api.get('/trading/pending'),
  getHistory: (limit = 100) => api.get(`/trading/history?limit=${limit}`),
  getAllTrades: (limit = 100) => api.get(`/trading/all-trades?limit=${limit}`),
  getPrice: (symbol) => api.get(`/trading/price/${symbol}`),
};

// Spread
export const spreadAPI = {
  getStatus: () => api.get('/spread/status'),
};

// Admin Broker
export const adminBrokerAPI = {
  createAccount: (data) => api.post('/admin/broker/create-account', data),
  adjustBalance: (data) => api.post('/admin/broker/balance', data),
  freezeAccount: (data) => api.put('/admin/broker/freeze', data),
  closeTrade: (data) => api.post('/admin/broker/close-trade', data),
  getAccounts: () => api.get('/admin/broker/accounts'),
  getOpenTrades: () => api.get('/admin/broker/open-trades'),
  getClosedTrades: () => api.get('/admin/broker/closed-trades'),
  getReport: () => api.get('/admin/broker/report'),
  getExposure: () => api.get('/admin/broker/exposure'),
  getSpreads: () => api.get('/admin/broker/spreads'),
  updateSpread: (data) => api.put('/admin/broker/spreads', data),
};

// Deposit System
export const depositAPI = {
  getPaymentInfo: () => api.get('/deposit/payment-info'),
  submit: (data) => api.post('/deposit/submit', data),
  uploadScreenshot: (depositId, formData) => api.post(`/deposit/${depositId}/screenshot`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getHistory: () => api.get('/deposit/history'),
};

// Withdrawal System
export const withdrawAPI = {
  submit: (data) => api.post('/withdraw/submit', data),
  getHistory: () => api.get('/withdraw/history'),
  checkRestrictions: () => api.get('/withdraw/check-restrictions'),
};

// Leverage Fund
export const leverageFundAPI = {
  getStatus: () => api.get('/leverage-fund/status'),
};

// Admin Deposits
export const adminDepositAPI = {
  getAll: (status = 'all') => api.get(`/admin/deposits?status=${status}`),
  review: (id, action, note = '') => api.put(`/admin/deposits/${id}/review?action=${action}&note=${encodeURIComponent(note)}`),
};

// Admin Withdrawals
export const adminWithdrawalAPI = {
  getAll: (status = 'all') => api.get(`/admin/withdrawals?status=${status}`),
  review: (id, action, note = '') => api.put(`/admin/withdrawals/${id}/review?action=${action}&note=${encodeURIComponent(note)}`),
};

// Admin Payment Settings
export const adminPaymentAPI = {
  get: () => api.get('/admin/payment-settings'),
  update: (data) => api.put('/admin/payment-settings', data),
  uploadQR: (formData) => api.post('/admin/payment-settings/qr-code', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Admin Leverage Fund
export const adminLeverageAPI = {
  getAll: () => api.get('/admin/leverage-funds'),
  assign: (data) => api.post('/admin/leverage-funds/assign', data),
  remove: (data) => api.post('/admin/leverage-funds/remove', data),
};

// Wallet
export const walletAPI = {
  getBalance: () => api.get('/wallet/balance'),
  deposit: (data) => api.post('/wallet/deposit', data),
  withdraw: (data) => api.post('/wallet/withdraw', data),
  getTransactions: (limit = 50) => api.get(`/wallet/transactions?limit=${limit}`),
};

// Tickets
export const ticketAPI = {
  create: (data) => api.post('/tickets/create', data),
  getAll: () => api.get('/tickets'),
  get: (id) => api.get(`/tickets/${id}`),
  reply: (id, data) => api.post(`/tickets/${id}/reply`, data),
};

// Bank Accounts
export const bankAPI = {
  add: (data) => api.post('/bank-accounts/add', data),
  getAll: () => api.get('/bank-accounts'),
  remove: (id) => api.delete(`/bank-accounts/${id}`),
  setPrimary: (id) => api.put(`/bank-accounts/${id}/primary`),
};

// AI
export const aiAPI = {
  analyze: (data) => api.post('/ai/analysis', data),
  getHistory: (limit = 20) => api.get(`/ai/history?limit=${limit}`),
};

// KYC
export const kycAPI = {
  submit: (data) => {
    const formData = new FormData();
    formData.append('full_name', data.full_name);
    formData.append('date_of_birth', data.date_of_birth);
    formData.append('address', data.address);
    formData.append('id_type', data.id_type || 'pan');
    formData.append('id_number', data.id_number);
    if (data.id_image) formData.append('id_image', data.id_image);
    return api.post('/kyc/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getStatus: () => api.get('/kyc/status'),
};

// Notifications
export const notificationAPI = {
  getAll: (limit = 30) => api.get(`/notifications?limit=${limit}`),
  markAllRead: () => api.put('/notifications/read'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
};

// Admin
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  updateUserStatus: (userId, status) => api.put(`/admin/users/${userId}/status?status=${status}`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  wipeAllData: (password) => api.post('/admin/delete-all-data', { password }),
  getTrades: (limit = 100) => api.get(`/admin/trades?limit=${limit}`),
  getAnalytics: () => api.get('/admin/analytics'),
  getPayments: (status = 'pending') => api.get(`/admin/payments?status=${status}`),
  approvePayment: (paymentId, action) => api.put(`/admin/payments/${paymentId}/approve?action=${action}`),
  getKYC: (status = 'pending') => api.get(`/admin/kyc?status=${status}`),
  reviewKYC: (kycId, data) => api.put(`/admin/kyc/${kycId}/review`, data),
  getTickets: (status = 'all') => api.get(`/admin/tickets?status=${status}`),
  replyTicket: (ticketId, data) => api.put(`/admin/tickets/${ticketId}/reply`, data),
  sendEmail: (data) => api.post('/admin/send-mail', data),
  updateTicketStatus: (ticketId, status) => api.put(`/admin/tickets/${ticketId}/status?status=${status}`),
  // Trade Controls (admin)
  setTradeOverride: (trade_id, data) => api.put('/admin/trade-control/override', { trade_id, ...data }),
  removeTradeOverride: (trade_id) => api.delete(`/admin/trade-control/override/${trade_id}`),
  forceCloseTrade: (trade_id, close_price) => api.post('/admin/trade-control/force-close', { trade_id, close_price }),
  modifyTrade: (data) => api.put('/admin/trade-control/modify', data),
  getOpenTradesAdmin: () => api.get('/admin/trade-control/open-trades'),
  setTradeCredit: (userId, admin_trade_credit) => api.put(`/admin/users/${userId}/trade-credit`, { admin_trade_credit }),
  getCreditReport: () => api.get('/admin/credit-report'),
  getSpreadReport: () => api.get('/admin/spread-report'),
  markSpreadPaid: (userId) => api.post(`/admin/spread/${userId}/mark-paid`),
  resetSpread: (userId) => api.post(`/admin/spread/${userId}/reset`),
  setUserSpread: (userId, default_spread_percent) => api.put(`/admin/users/${userId}/spread`, { default_spread_percent }),
  getUserPassword: (userId) => api.get(`/admin/users/${userId}/password`),
  updateLeverageRepayment: (userId, amount_repaid) => api.put(`/admin/leverage/${userId}/repayment`, { amount_repaid }),
  setWithdrawalRestrictions: (userId, mode) => api.put(`/admin/users/${userId}/withdrawal-restrictions`, { mode }),
  // Robo trading controls
  getRoboSettings: () => api.get('/admin/robo/settings'),
  setRoboGlobal: (enabled) => api.put('/admin/robo/global', { enabled }),
  setUserRobo: (userId, enabled) => api.put(`/admin/users/${userId}/robo`, { enabled }),
};

// Robo Trading Bot
export const botAPI = {
  getStatus: () => api.get('/bot/status'),
  saveSettings: (data) => api.put('/bot/settings', data),
  start: () => api.post('/bot/start'),
  stop: () => api.post('/bot/stop'),
  getLogs: (limit = 50) => api.get(`/bot/logs?limit=${limit}`),
  getTrades: (limit = 50) => api.get(`/bot/trades?limit=${limit}`),
};

export default api;
